const TENANT_JOIN_PREFIX = '/tenant/join';
const OIDC_AUTHORIZATION_PATHS = new Set(['/oauth', '/oauth/login', '/oauth/consent']);
const AUTH_FLOW_PATHS = new Set(['/login', '/oauth2/callback', '/tenant-selector']);

export type TenantSelectorMode = 'auto' | 'switch';

export const TENANT_SELECTOR_SWITCH_PATH = '/tenant-selector?mode=switch';

/** resolveTenantSelectorMode 把租户选择页查询参数收口为自动进入或主动切换语义。 */
export function resolveTenantSelectorMode(search?: string): TenantSelectorMode {
  return new URLSearchParams(search).get('mode') === 'switch' ? 'switch' : 'auto';
}

/** normalizeInternalRedirect 只接受当前站点的绝对路径，避免认证完成后跳向外部域名或协议相对地址。 */
export function normalizeInternalRedirect(
  target?: string | null,
  origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
): string | undefined {
  const value = target?.trim();
  if (!value || !value.startsWith('/') || value.startsWith('//')) return undefined;

  try {
    const url = new URL(value, origin);
    if (url.origin !== origin) return undefined;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

/** normalizeAuthFlowRedirect 排除认证中转页，避免登录完成后把登录页或租户选择页再次当成最终目标。 */
export function normalizeAuthFlowRedirect(
  target?: string | null,
  origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
): string | undefined {
  const normalized = normalizeInternalRedirect(target, origin);
  if (!normalized) return undefined;
  const pathname = normalized.split(/[?#]/, 1)[0];
  const comparablePath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return AUTH_FLOW_PATHS.has(comparablePath) ? undefined : normalized;
}

/** isTenantJoinRedirect 判断安全站内地址是否属于邀请或申请加入租户流程。 */
export function isTenantJoinRedirect(target?: string | null): boolean {
  const normalized = normalizeInternalRedirect(target);
  if (!normalized) return false;
  const pathname = normalized.split(/[?#]/, 1)[0];
  return pathname === TENANT_JOIN_PREFIX || pathname.startsWith(`${TENANT_JOIN_PREFIX}/`);
}

/** isOIDCAuthorizationRedirect 判断站内地址是否属于需要平台用户态的 Hydra 授权页面。 */
export function isOIDCAuthorizationRedirect(target?: string | null): boolean {
  const normalized = normalizeInternalRedirect(target);
  if (!normalized) return false;
  const pathname = normalized.split(/[?#]/, 1)[0];
  const comparablePath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return OIDC_AUTHORIZATION_PATHS.has(comparablePath);
}

/** isPlatformUserRedirect 标识无需租户员工态即可继续的登录后目标。 */
export function isPlatformUserRedirect(target?: string | null): boolean {
  return isTenantJoinRedirect(target) || isOIDCAuthorizationRedirect(target);
}

/**
 * normalizeOIDCServerRedirect 只接受站内路径或显式 HTTP(S) 地址，避免把后端异常值执行为脚本协议。
 */
export function normalizeOIDCServerRedirect(
  target?: string | null,
  origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
): string | undefined {
  const value = target?.trim();
  if (!value) return undefined;

  const candidates = [value];
  try {
    const decoded = decodeURIComponent(value);
    if (decoded !== value) candidates.push(decoded);
  } catch {
    // 非 URI 编码文本继续按原值校验即可。
  }

  for (const candidate of candidates) {
    const internal = normalizeInternalRedirect(candidate, origin);
    if (internal) return internal;
    if (!/^https?:\/\//i.test(candidate)) continue;

    try {
      const url = new URL(candidate);
      if (url.protocol === 'http:' || url.protocol === 'https:') return url.toString();
    } catch {
      // 当前候选值无效时继续尝试兼容解码后的候选值。
    }
  }

  return undefined;
}

/** extractTenantIDFromRedirect 从安全租户深链提取租户 ID，非租户地址返回空字符串。 */
export function extractTenantIDFromRedirect(target?: string | null): string {
  const normalized = normalizeInternalRedirect(target);
  if (!normalized) return '';

  try {
    const url = new URL(normalized, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    return parts[0] === 't' ? decodeURIComponent(parts[1] ?? '') : '';
  } catch {
    return '';
  }
}

/** replaceTenantIDInRedirect 替换租户深链前缀并完整保留业务查询与哈希。 */
export function replaceTenantIDInRedirect(target: string, tenantID: string): string {
  const normalized = normalizeInternalRedirect(target);
  if (!normalized) return `/t/${encodeURIComponent(tenantID)}/workspace`;

  try {
    const url = new URL(normalized, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    if (parts[0] === 't' && parts.length >= 2) {
      parts[1] = encodeURIComponent(tenantID);
      return `/${parts.join('/')}${url.search}${url.hash}`;
    }
  } catch {
    return `/t/${encodeURIComponent(tenantID)}/workspace`;
  }
  return `/t/${encodeURIComponent(tenantID)}/workspace`;
}
